import { createHttpCore } from './http';
import type { HttpCore, HttpCoreOptions } from './http';
import {
  createAdminApi,
  createAlertsApi,
  createAuthApi,
  createDeviceDataApi,
  createDevicesApi,
  createMeApi,
  createNotificationsApi,
  createProvidersApi,
  createFeedbackApi,
  createPushApi,
  createSubscriptionApi,
  createTenantApi,
} from './endpoints';
import type { RuntimeConfig } from './config';

export type ApiClientOptions = HttpCoreOptions;

/**
 * The whole SolarFox API, grouped by area. `http` is the escape hatch for endpoints that land after
 * this file was written — prefer adding a typed function to `src/endpoints/*` instead.
 */
export interface ApiClient extends Pick<
  HttpCore,
  | 'kind'
  | 'baseUrl'
  | 'tenantSlug'
  | 'getTokens'
  | 'setTokens'
  | 'isAuthenticated'
  | 'onTokensChanged'
  | 'onDemoReadOnly'
> {
  http: HttpCore;
  auth: ReturnType<typeof createAuthApi>;
  me: ReturnType<typeof createMeApi>;
  tenant: ReturnType<typeof createTenantApi>;
  providers: ReturnType<typeof createProvidersApi>;
  devices: ReturnType<typeof createDevicesApi>;
  deviceData: ReturnType<typeof createDeviceDataApi>;
  alerts: ReturnType<typeof createAlertsApi>;
  notifications: ReturnType<typeof createNotificationsApi>;
  feedback: ReturnType<typeof createFeedbackApi>;
  push: ReturnType<typeof createPushApi>;
  subscription: ReturnType<typeof createSubscriptionApi>;
  admin: ReturnType<typeof createAdminApi>;
}

/**
 * Creates an API client.
 *
 * ```ts
 * const api = createApiClient({ baseUrl: config.apiBaseUrl, tenant: config.tenant });
 * const { user } = await api.auth.login({ email, password });   // tokens are stored automatically
 * const devices = await api.devices.list();
 * ```
 *
 * `kind` decides which token slot is used (`sf.customer.tokens` vs `sf.admin.tokens`) and which
 * refresh endpoint the 401 retry calls. Use one client per app.
 */
export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const http = createHttpCore(options);
  return {
    http,
    kind: http.kind,
    baseUrl: http.baseUrl,
    tenantSlug: http.tenantSlug,
    getTokens: http.getTokens,
    setTokens: http.setTokens,
    isAuthenticated: http.isAuthenticated,
    onTokensChanged: http.onTokensChanged,
    onDemoReadOnly: http.onDemoReadOnly,
    auth: createAuthApi(http),
    me: createMeApi(http),
    tenant: createTenantApi(http),
    providers: createProvidersApi(http),
    devices: createDevicesApi(http),
    deviceData: createDeviceDataApi(http),
    alerts: createAlertsApi(http),
    notifications: createNotificationsApi(http),
    feedback: createFeedbackApi(http),
    push: createPushApi(http),
    subscription: createSubscriptionApi(http),
    admin: createAdminApi(http),
  };
}

/** Customer portal client (token slot `sf.customer.tokens`). */
export function createCustomerClient(config: RuntimeConfig, extra: Omit<ApiClientOptions, 'kind'> = {}) {
  return createApiClient({ ...extra, kind: 'customer', baseUrl: config.apiBaseUrl, tenant: config.tenant });
}

/** Admin portal client (token slot `sf.admin.tokens`, admin JWT audience). */
export function createAdminClient(config: RuntimeConfig, extra: Omit<ApiClientOptions, 'kind'> = {}) {
  return createApiClient({ ...extra, kind: 'admin', baseUrl: config.apiBaseUrl, tenant: config.tenant });
}
